import { useState, useCallback } from 'react';
import { AnnotationMarker } from '../types';
import { getPositionLabel } from '../utils/positionLabel';

interface UseAnnotationStateReturn {
  markers: AnnotationMarker[];
  activeMarkerId: string | null;
  setActiveMarkerId: (id: string | null) => void;
  addMarker: (x: number, y: number) => void;
  updateMarkerDescription: (id: string, description: string) => void;
  moveMarker: (id: string, x: number, y: number) => void;
  deleteMarker: (id: string) => void;
  clearAll: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 11);
}

export function useAnnotationState(): UseAnnotationStateReturn {
  const [markers, setMarkers] = useState<AnnotationMarker[]>([]);
  const [history, setHistory] = useState<AnnotationMarker[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);

  const pushHistory = useCallback(
    (newMarkers: AnnotationMarker[]) => {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(newMarkers);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    },
    [history, historyIndex],
  );

  const addMarker = useCallback(
    (x: number, y: number) => {
      const newMarker: AnnotationMarker = {
        id: generateId(),
        number: markers.length + 1,
        position: { x, y },
        label: getPositionLabel(x, y),
        description: '',
        createdAt: Date.now(),
      };

      const newMarkers = [...markers, newMarker];
      setMarkers(newMarkers);
      pushHistory(newMarkers);
      setActiveMarkerId(newMarker.id);
    },
    [markers, pushHistory],
  );

  const updateMarkerDescription = useCallback(
    (id: string, description: string) => {
      setMarkers((prev) =>
        prev.map((m) => (m.id === id ? { ...m, description } : m)),
      );
    },
    [],
  );

  const moveMarker = useCallback((id: string, x: number, y: number) => {
    setMarkers((prev) =>
      prev.map((m) =>
        m.id === id
          ? { ...m, position: { x, y }, label: getPositionLabel(x, y) }
          : m,
      ),
    );
  }, []);

  const deleteMarker = useCallback(
    (id: string) => {
      const newMarkers = markers
        .filter((m) => m.id !== id)
        .map((m, i) => ({ ...m, number: i + 1 }));
      setMarkers(newMarkers);
      pushHistory(newMarkers);
      if (activeMarkerId === id) {
        setActiveMarkerId(null);
      }
    },
    [markers, activeMarkerId, pushHistory],
  );

  const clearAll = useCallback(() => {
    setMarkers([]);
    pushHistory([]);
    setActiveMarkerId(null);
  }, [pushHistory]);

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setMarkers(history[newIndex]);
      setActiveMarkerId(null);
    }
  }, [history, historyIndex]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setMarkers(history[newIndex]);
      setActiveMarkerId(null);
    }
  }, [history, historyIndex]);

  return {
    markers,
    activeMarkerId,
    setActiveMarkerId,
    addMarker,
    updateMarkerDescription,
    moveMarker,
    deleteMarker,
    clearAll,
    undo,
    redo,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
  };
}
